import { useEffect, useRef, useState, type MouseEvent } from 'react';
import type { BombiixHeaderParams, BombiixRow } from '../types';
import { MoreIcon } from '../../../bombiix-icon';
import TruncatedText from '../../TruncatedText/TruncatedText';

/**
 * Custom AG-Grid header that matches the Figma "Costing" header:
 *   [label] [sort-arrow?]                         [kebab menu]
 *
 * Click on the label area toggles sort (asc → desc → none). Click on the kebab
 * opens AG Grid's built-in column menu.
 */
function SortableHeader<TRow extends BombiixRow = BombiixRow>(props: BombiixHeaderParams<TRow>) {
  const { displayName, column, api, showColumnMenu, required: requiredProp, dppRequired: dppRequiredProp } = props;

  const colDef = column?.getColDef?.() ?? {};
  const required = requiredProp ?? (colDef as { required?: boolean }).required ?? false;
  const dppRequired = dppRequiredProp ?? (colDef as { dppRequired?: boolean }).dppRequired ?? false;
  const enableSorting = colDef.sortable !== false;
  const showMenu = !colDef.suppressHeaderMenuButton;

  const [sort, setSort] = useState<string | null>(column?.getSort?.() ?? null);
  const menuBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!column) return undefined;
    const onSortChanged = () => setSort(column.getSort() ?? null);
    column.addEventListener('sortChanged', onSortChanged);
    return () => column.removeEventListener('sortChanged', onSortChanged);
  }, [column]);

  const handleLabelClick = () => {
    if (!enableSorting || !column || !api) return;
    const curr = column.getSort();
    const next = curr === 'asc' ? 'desc' : curr === 'desc' ? null : 'asc';
    api.applyColumnState({
      state: next ? [{ colId: column.getColId(), sort: next }] : [],
      defaultState: { sort: null },
    });
  };

  const handleMenuClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (showColumnMenu && menuBtnRef.current) {
      showColumnMenu(menuBtnRef.current);
    }
  };

  return (
    <div className="cag-header">
      <button type="button" className="cag-header-label-btn" onClick={handleLabelClick} disabled={!enableSorting}>
        <span className="cag-header-label">
          <TruncatedText text={displayName} />
          {required || dppRequired ? <span className={`cag-header-required${dppRequired ? ' cag-header-required--green' : ''}`}>*</span> : null}
        </span>
        <span className="ag-sort-indicator-container">
          <span className={`ag-sort-indicator-icon ag-sort-ascending-icon${sort === 'asc' ? '' : ' ag-hidden'}`}>
            <span className="ag-icon ag-icon-asc" role="presentation" />
          </span>
          <span className={`ag-sort-indicator-icon ag-sort-descending-icon${sort === 'desc' ? '' : ' ag-hidden'}`}>
            <span className="ag-icon ag-icon-desc" role="presentation" />
          </span>
        </span>
      </button>
      {showMenu ? (
        <button ref={menuBtnRef} type="button" className="cag-header-menu-btn" onClick={handleMenuClick} aria-label="Column menu" title="Column menu">
          <MoreIcon size={20} />
        </button>
      ) : null}
    </div>
  );
}

export default SortableHeader;
